import { Component, OnInit, OnDestroy, Output, EventEmitter } from '@angular/core';
import { Subscription, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { UsersService } from 'src/app/services/users.service';
import { ToastService } from 'src/app/services/toast.service';
import { AuthService } from 'src/app/services/auth.service';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-secondary-manager-reassignment',
  templateUrl: './secondary-manager-reassignment.component.html',
  styleUrls: ['./secondary-manager-reassignment.component.scss'],
})
export class SecondaryManagerReassignmentComponent implements OnInit, OnDestroy {
  @Output() goBack = new EventEmitter<void>();

  // Secondary Manager workflow properties
  selectedNewSecondaryManager: any = null;
  selectedFilterSecondaryManager: any = null;
  managerAssignmentType: 'move' | 'add' = 'move';

  // User list properties
  userList: any[] = [];
  filteredUserList: any[] = [];
  selectedUsers: any[] = [];
  userSearchText = '';
  selectAllUsers = false;

  // Secondary Managers properties
  secondaryManagers: any[] = [];

  // Progress tracking
  isProcessing = false;
  progressMessage = '';
  progressPercentage = 0;
  estimatedTimeRemaining = 0;
  isLoadingStaff = false;

  // Helper method for template
  Math = Math;

  // Subscriptions
  private subscriptions: Subscription[] = [];

  constructor(
    private http: HttpClient,
    private usersService: UsersService,
    private toastService: ToastService,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    this.loadSecondaryManagers();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  onBackClick(): void {
    this.reset();
    this.goBack.emit();
  }

  // Load secondary managers using users API with specific role filtering
  loadSecondaryManagers = () => {
    this.authService.user$.subscribe((user: any) => {
      if (!user || !user.loginName) {
        return;
      }

      const apiUrl = `${environment.expressUrl}/users`;
      const params = {
        loginName: user.loginName,
        activeFlag: 1,
        includedAssociations: 'PrimaryBuilding, PrimaryPosition, Roles',
        limit: 10000,
        offset: 0,
        sortColumnName: 'firstName',
        sortColumnDirection: 'ASC',
        showAssignedToMe: 0,
        customFlag: 0,
      };

      this.http.get(apiUrl, { params }).subscribe({
        next: (response: any) => {
          let users = null;
          if (response?.rows) {
            users = response.rows;
          } else if (response?.data?.rows) {
            users = response.data.rows;
          } else if (response?.data && Array.isArray(response.data)) {
            users = response.data;
          } else if (Array.isArray(response)) {
            users = response;
          }

          if (users && users.length > 0) {
            this.secondaryManagers = users.filter((user: any) => {
              return user.Roles?.some((role: any) => {
                const roleName = role.roleName?.toLowerCase() || '';
                const accessLevel = role.accessLevel || '';

                return (
                  accessLevel === 'MANAGE_ALL_AUTHORITY' ||
                  accessLevel === 'MANAGE_SECONDARY_AUTHORITY' ||
                  (roleName === 'default supervisor' &&
                    accessLevel === 'MANAGE_SECONDARY_AUTHORITY') ||
                  (roleName === 'default administrator' &&
                    accessLevel === 'MANAGE_SECONDARY_AUTHORITY')
                );
              });
            });
          }
        },
        error: (error) => {
          console.error('Error getting secondary managers:', error);
        },
      });
    });
  };

  // Secondary Manager selection methods
  onNewSecondaryManagerSelected = (manager: any) => {
    this.selectedNewSecondaryManager = manager;
  };

  onFilterSecondaryManagerSelected = (manager: any) => {
    this.selectedFilterSecondaryManager = manager;
    this.loadStaffFromSecondaryManager(manager);
  };

  loadStaffFromSecondaryManager = (manager: any) => {
    this.isLoadingStaff = true;
    this.userList = [];
    this.filteredUserList = [];
    this.selectedUsers = [];
    this.selectAllUsers = false;
    this.userSearchText = '';

    this.authService.user$.subscribe((user: any) => {
      if (!user || !user.loginName) {
        this.isLoadingStaff = false;
        return;
      }

      const apiUrl = `${environment.expressUrl}/users`;
      const params = {
        loginName: user.loginName,
        activeFlag: 1,
        includedAssociations:
          'PrimaryBuilding, PrimaryPosition, Roles, SecondaryApprovers',
        limit: 10000,
        offset: 0,
        sortColumnName: 'firstName',
        sortColumnDirection: 'ASC',
        showAssignedToMe: 0,
        customFlag: 0,
      };

      this.http.get(apiUrl, { params }).subscribe({
        next: (response: any) => {
          let users = null;
          if (response?.rows) {
            users = response.rows;
          } else if (response?.data?.rows) {
            users = response.data.rows;
          } else if (response?.data && Array.isArray(response.data)) {
            users = response.data;
          } else if (Array.isArray(response)) {
            users = response;
          }

          if (users && users.length > 0) {
            const activeUsers = users.filter(
              (user: any) => user.activeFlag === 1
            );
            this.loadUserDetailsWithSecondaryApproversAndFilter(
              activeUsers,
              manager
            );
          } else {
            this.isLoadingStaff = false;
          }
        },
        error: (error) => {
          console.error(
            'Error fetching users for secondary staff filtering:',
            error
          );
          this.isLoadingStaff = false;
        },
      });
    });
  };

  // Load detailed user information including secondary approvers for each user and filter by manager
  loadUserDetailsWithSecondaryApproversAndFilter = (
    users: any[],
    manager: any
  ) => {
    if (users.length === 0) {
      this.userList = [];
      this.filteredUserList = [];
      this.isLoadingStaff = false;
      return;
    }

    const usersWithApprovers = users.filter(
      (user) => user.SecondaryApprovers && Array.isArray(user.SecondaryApprovers)
    );
    const usersWithoutApprovers = users.filter(
      (user) => !user.SecondaryApprovers || !Array.isArray(user.SecondaryApprovers)
    );

    const managedUsersFromExisting = usersWithApprovers.filter((user: any) => {
      return user.SecondaryApprovers.some(
        (approver: any) =>
          approver &&
          approver.id &&
          (approver.id === manager.id || approver.id === Number(manager.id))
      );
    });

    if (usersWithoutApprovers.length > 0) {
      const userDetailPromises = usersWithoutApprovers.map((user: any) => {
        return this.http
          .get(`${environment.expressUrl}/users/${user.id}`, {
            headers: { 'Content-Type': 'application/vnd.api+json' },
            params: {
              loginName: this.usersService.user?.loginName,
              includedAssociations: 'PrimaryApprovers, SecondaryApprovers',
            },
          })
          .pipe(
            map((response: any) => {
              if (response?.data) {
                return {
                  ...user,
                  ...response.data,
                };
              }
              return user;
            }),
            catchError(() => {
              return of(user);
            })
          )
          .toPromise();
      });

      Promise.all(userDetailPromises).then((detailedUsers: any[]) => {
        const validDetailedUsers = detailedUsers.filter(
          (user) => user !== null
        );
        const managedUsersFromFetch = validDetailedUsers.filter(
          (user: any) => {
            if (
              user.SecondaryApprovers &&
              Array.isArray(user.SecondaryApprovers)
            ) {
              return user.SecondaryApprovers.some(
                (approver: any) =>
                  approver &&
                  approver.id &&
                  (approver.id === manager.id ||
                    approver.id === Number(manager.id))
              );
            }
            return false;
          }
        );

        const allManagedUsers = [
          ...managedUsersFromExisting,
          ...managedUsersFromFetch,
        ];

        this.userList = allManagedUsers;
        this.filteredUserList = [...this.userList];
        this.isLoadingStaff = false;
      });
    } else {
      this.userList = managedUsersFromExisting;
      this.filteredUserList = [...this.userList];
      this.isLoadingStaff = false;
    }
  };

  // User search functionality
  onUserSearchChange = (searchText: string) => {
    this.userSearchText = searchText;

    if (!searchText || searchText.trim() === '') {
      this.filteredUserList = [...this.userList];
    } else {
      this.filteredUserList = this.userList.filter(
        (user) =>
          user.firstName?.toLowerCase().includes(searchText.toLowerCase()) ||
          user.lastName?.toLowerCase().includes(searchText.toLowerCase()) ||
          user.email?.toLowerCase().includes(searchText.toLowerCase())
      );
    }
  };

  // User selection methods
  toggleUserSelection = (user: any) => {
    const index = this.selectedUsers.findIndex((u) => u.id === user.id);
    if (index > -1) {
      this.selectedUsers.splice(index, 1);
    } else {
      this.selectedUsers.push(user);
    }
  };

  toggleSelectAllUsers = () => {
    this.selectAllUsers = !this.selectAllUsers;
    if (this.selectAllUsers) {
      this.selectedUsers = [...this.filteredUserList];
    } else {
      this.selectedUsers = [];
    }
  };

  isUserSelected = (user: any) => {
    return this.selectedUsers.some((u) => u.id === user.id);
  };

  // Assign secondary manager to selected users
  assignSecondaryManagerToUsers = () => {
    if (this.selectedUsers.length === 0 || !this.selectedNewSecondaryManager) {
      this.toastService.setToast({
        text: 'Please select both users and a secondary manager to assign.',
        type: 'warn',
        icon: true,
      });
      return;
    }

    this.isProcessing = true;
    this.progressPercentage = 0;
    this.estimatedTimeRemaining = Math.max(5, this.selectedUsers.length * 0.5);
    this.progressMessage = `Processing bulk secondary manager assignment for ${this.selectedUsers.length} users...`;

    setTimeout(() => {
      const progressElement = document.getElementById('progress-indicator');
      if (progressElement) {
        progressElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);

    const progressInterval = setInterval(() => {
      if (this.isProcessing) {
        this.progressPercentage = Math.min(
          90,
          this.progressPercentage + Math.random() * 15
        );
        this.estimatedTimeRemaining = Math.max(
          0,
          this.estimatedTimeRemaining - 1
        );

        this.progressMessage = `Processing bulk secondary manager assignment for ${
          this.selectedUsers.length
        } users... ${Math.round(this.progressPercentage)}% complete`;
      }
    }, 500);

    const beforeUnloadHandler = (event: BeforeUnloadEvent) => {
      if (this.isProcessing) {
        event.preventDefault();
        event.returnValue =
          'Bulk secondary manager assignment in progress. Are you sure you want to leave?';
        return 'Bulk secondary manager assignment in progress. Are you sure you want to leave?';
      }
    };
    window.addEventListener('beforeunload', beforeUnloadHandler);

    const userIds = [
      ...new Set(this.selectedUsers.map((user: any) => Number(user.id))),
    ];
    const managerId = Number(this.selectedNewSecondaryManager.id);

    const requestBody: any = { loginName: this.usersService.user?.loginName };

    if (this.managerAssignmentType === 'move') {
      const allCurrentSecondaryApproverIds = new Set<number>();

      this.selectedUsers.forEach((user: any) => {
        if (user.SecondaryApprovers && Array.isArray(user.SecondaryApprovers)) {
          user.SecondaryApprovers.forEach((approver: any) => {
            if (approver && approver.id) {
              allCurrentSecondaryApproverIds.add(Number(approver.id));
            }
          });
        }
      });

      const approversToRemove = Array.from(
        allCurrentSecondaryApproverIds
      ).filter((approverId: number) => approverId !== managerId);

      if (approversToRemove.length > 0) {
        requestBody.removeSecondaryApproverIds = approversToRemove;
      }

      requestBody.addSecondaryApproverIds = [managerId];

      if (
        approversToRemove.length === 0 &&
        allCurrentSecondaryApproverIds.has(managerId)
      ) {
        this.toastService.setToast({
          text: `All selected users are already assigned to "${this.selectedNewSecondaryManager.firstName} ${this.selectedNewSecondaryManager.lastName}". No changes were made.`,
          type: 'info',
          icon: true,
        });
        this.isProcessing = false;
        this.progressPercentage = 0;
        this.progressMessage = '';
        return;
      }
    } else {
      requestBody.addSecondaryApproverIds = [managerId];
    }

    this.processBatchedSecondaryManagerUpdates(
      userIds,
      requestBody,
      progressInterval,
      beforeUnloadHandler
    );
  };

  // Process batched secondary manager updates
  private async processBatchedSecondaryManagerUpdates(
    userIds: number[],
    requestBody: any,
    progressInterval: any,
    beforeUnloadHandler: any
  ) {
    try {
      if (
        this.managerAssignmentType === 'move' &&
        requestBody.removeSecondaryApproverIds?.length > 0
      ) {
        const removeRequestBody = {
          loginName: this.usersService.user?.loginName,
          removeSecondaryApproverIds: requestBody.removeSecondaryApproverIds,
        };

        this.progressMessage = `Removing current secondary managers from ${userIds.length} users...`;
        await this.batchUserUpdates(userIds, removeRequestBody, true);

        const addRequestBody = {
          loginName: this.usersService.user?.loginName,
          addSecondaryApproverIds: requestBody.addSecondaryApproverIds,
        };

        this.progressMessage = `Adding new secondary manager to ${userIds.length} users...`;
        await this.batchUserUpdates(userIds, addRequestBody, false);
      } else {
        this.progressMessage = `Adding new secondary manager to ${userIds.length} users...`;
        await this.batchUserUpdates(userIds, requestBody, false);
      }

      clearInterval(progressInterval);
      window.removeEventListener('beforeunload', beforeUnloadHandler);

      this.progressPercentage = 100;
      this.progressMessage = 'Bulk secondary manager assignment completed successfully!';

      setTimeout(() => {
        this.isProcessing = false;
        this.progressMessage = '';
        this.progressPercentage = 0;
      }, 1000);

      this.toastService.setToast({
        text: `✅ Successfully ${this.managerAssignmentType === 'move' ? 'moved' : 'added'} ${this.selectedUsers.length} staff member(s) to "${this.selectedNewSecondaryManager.firstName} ${this.selectedNewSecondaryManager.lastName}"`,
        type: 'success',
        icon: true,
      });

      this.usersService.clearSelectedUser();
      this.reset();
      this.goBack.emit();
    } catch (error: any) {
      clearInterval(progressInterval);
      window.removeEventListener('beforeunload', beforeUnloadHandler);
      this.isProcessing = false;
      this.progressMessage = '';

      console.error('Error in bulk secondary manager assignment:', error);
      this.toastService.setToast({
        text: `❌ Failed to assign secondary manager "${this.selectedNewSecondaryManager.firstName} ${this.selectedNewSecondaryManager.lastName}" to users. Please try again.`,
        type: 'error',
        icon: true,
      });
    }
  }

  // Utility function to batch user updates in groups of 10
  private async batchUserUpdates(
    userIds: number[],
    requestBody: any,
    isRemoveOperation = false,
    batchSize = 10
  ): Promise<any[]> {
    const results: any[] = [];
    const batches = this.chunkArray(userIds, batchSize);
    let totalUsersProcessed = 0;

    for (let i = 0; i < batches.length; i++) {
      const batch = batches[i];
      const batchPromises = batch.map((userId: number) => {
        return this.usersService.updateUser(requestBody, userId).toPromise();
      });

      try {
        const batchResults = await Promise.all(batchPromises);
        results.push(...batchResults);

        totalUsersProcessed += batch.length;

        const progressPercent = Math.round(
          (totalUsersProcessed / userIds.length) * 90
        );

        this.progressPercentage = Math.max(
          this.progressPercentage,
          progressPercent
        );
        this.progressMessage = `Processing ${totalUsersProcessed} of ${userIds.length} users... ${this.progressPercentage}% complete`;

        if (i < batches.length - 1) {
          await new Promise((resolve) => setTimeout(resolve, 100));
        }
      } catch (error) {
        console.error(`Error in batch ${i + 1}:`, error);
        throw error;
      }
    }

    return results;
  }

  // Utility function to chunk array into smaller arrays
  private chunkArray<T>(array: T[], size: number): T[][] {
    const chunks: T[][] = [];
    for (let i = 0; i < array.length; i += size) {
      chunks.push(array.slice(i, i + size));
    }
    return chunks;
  }

  reset = () => {
    this.selectedNewSecondaryManager = null;
    this.selectedFilterSecondaryManager = null;
    this.userList = [];
    this.filteredUserList = [];
    this.selectedUsers = [];
    this.userSearchText = '';
    this.selectAllUsers = false;
    this.secondaryManagers = [];
    this.isProcessing = false;
    this.progressMessage = '';
    this.progressPercentage = 0;
    this.estimatedTimeRemaining = 0;
    this.isLoadingStaff = false;
  };
}

